import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAssetUrl } from '../../utils/assetUtils';
import { getLatestPublicProjectId } from '../../utils/projectUtils';
import './Header.scss';

const Header = ({
  project,
  projects,
  onProjectSelect,
  showBackToTimeline,
  activeProjectId = null
}) => {
  const logoUrl = getAssetUrl('logo.svg');
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isHomeRoute = location.pathname === '/' && !searchParams.has('event');
  const resolvedProject = project || projects?.find((entry) => entry.id === activeProjectId) || null;
  const resolvedActiveProjectId = activeProjectId ?? (isHomeRoute ? 'home' : (resolvedProject?.id ?? null));

  const latestProjectId = getLatestPublicProjectId();
  const defaultCollectionRoute = latestProjectId ? `/${latestProjectId}` : '/';

  const scrollCollectionsToTop = () => {
    if (typeof document === 'undefined') return false;

    const scrollContainer = document.querySelector('.collection-carousels-container');
    if (!scrollContainer) return false;

    scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    return true;
  };

  const scrollCollectionsToAbout = () => {
    if (typeof document === 'undefined') return false;
    const aboutSection = document.querySelector('.collection-carousels-container .about-section');
    if (!aboutSection) return false;

    aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  };

  const scrollCollectionsToProject = (projectId) => {
    if (typeof document === 'undefined') return false;

    const targetSection = document.querySelector(`.collection-carousels-container section[id="${projectId}"]`);
    if (!targetSection) return false;

    targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  };

  const handleLogoClick = () => {
    if (scrollCollectionsToTop()) {
      return;
    }

    if (latestProjectId) {
      navigate(`/${latestProjectId}`, { state: { scrollToTop: true } });
    } else {
      navigate('/', { state: { scrollToTop: true } });
    }
  };

  const handleBackToTimeline = () => {
    navigate('/about');
  };

  const handleProjectClick = (projectId) => {
    onProjectSelect(projectId);
    setIsMenuOpen(false);
    if (projectId === 'about') {
      if (scrollCollectionsToAbout()) {
        window.history.replaceState(null, '', '/about');
      } else {
        navigate(defaultCollectionRoute, { state: { scrollToAbout: true } });
      }
      return;
    } else {
      if (scrollCollectionsToProject(projectId)) {
        window.history.replaceState(null, '', `/${projectId}`);
      } else {
        navigate(`/${projectId}`);
      }
    }
  };

  // Sort all public projects by descending order (latest first)
  const sortedProjects = projects ? [...projects]
    .filter(p => p.status === 'Public')
    .sort((a, b) => (b.order || 0) - (a.order || 0)) : [];
  const navigationProjects = sortedProjects;

  const isAboutActive = location.pathname === '/about';

  return (
    <motion.header
      className="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
    >
      <div className="header-logo-container">
        <img
          src={logoUrl}
          alt="Elodie Carstensen Logo"
          className="header-logo"
          onClick={handleLogoClick}
          style={{ cursor: 'pointer' }}
        />
      </div>
      {showBackToTimeline ? (
        <button className="back-to-timeline" onClick={handleBackToTimeline}>
          Back to timeline
        </button>
      ) : (
          resolvedProject && projects && projects.length > 0 && (
          <div
            className={`header-project-menu ${isMenuOpen ? 'open' : ''}`}
            onMouseEnter={() => setIsMenuOpen(true)}
            onMouseLeave={(e) => {
              // Only close if mouse actually left the entire menu area
              // Check if relatedTarget exists and is a Node before calling contains
              const relatedTarget = e.relatedTarget;
              if (!relatedTarget || !(relatedTarget instanceof Node) || !e.currentTarget.contains(relatedTarget)) {
                setIsMenuOpen(false);
              }
            }}
            role="navigation"
            aria-label="Project navigation"
          >
            <button
                className={`current-project ${(isAboutActive && resolvedProject.id === 'contact') ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="project-menu"
              aria-label="Toggle project menu"
            >
              <>
                <span className="project-name">{resolvedProject.name}</span>
                {resolvedProject.season && (
                  <>
                    <span className="project-separator">/</span>
                    <span className="project-season">{resolvedProject.season}</span>
                  </>
                )}
              </>
              <span className="menu-indicator" aria-hidden="true">
                  {navigationProjects.map((p) => (
                    <span
                      key={p.id}
                      className={`dot ${resolvedActiveProjectId === p.id ? 'active' : ''}`}
                    />
                  ))}
              </span>
            </button>
            <div
              id="project-menu"
              className={`project-options ${isMenuOpen ? 'open' : ''}`}
              role="menu"
              >
              {navigationProjects
                .map(p => {
                  // Prioritize project prop over location.pathname to avoid double-active states
                  // When project.id is set (from CollectionsPage currentSectionIndex), use only that
                  // Only use isAboutActive fallback when project prop is not available or explicitly 'about'
                  const isActive = resolvedActiveProjectId
                    ? (resolvedActiveProjectId === p.id)
                    : (isAboutActive && p.id === 'contact');

                  return (
                  <button
                    key={p.id}
                    className={`project-option ${isActive ? 'active' : ''}`}
                    onClick={() => handleProjectClick(p.id)}
                    role="menuitem"
                  >
                    <span className="project-name">{p.name}</span>
                    {p.season && (
                      <>
                        <span className="project-separator">/</span>
                        <span className="project-season">{p.season}</span>
                      </>
                    )}
                  </button>
                  );
                })}
            </div>
          </div>
          )
      )}
    </motion.header>
  );
};

export default Header;
